import { supabase } from '../lib/supabase';
import { booksChanged } from '../lib/changed';
import type { NoticeInput } from '../shell/notice';
import { addBook, undoAdd } from '../add/add';
import { runImport, undoImport } from '../mybooks/importLoad';
import { lookUpNow } from '../queue/lookupLoad';
import type { Literary } from '../book/genres';
import { fmtNum } from '../ui/ListFoot';
import { levelName, type ListEntry } from './lists';

// Adding books from a list (Discover Part 3b, 0057; agreed with Kristi Nov 3): one book through Add
// a book, started on it; Not for me ticked on a row, passed on in Discover at once, with Undo;
// several at once as an import (Past imports, Recently added's Came from, Undo import), to the
// bottom of Future reads unless you choose End of Up Next or No shelf yet. Entries not yet on the
// catalog are linked to the new books when they fit the list's rules.

type Notice = (n: NoticeInput) => void;

/**
 * "Booker Prize 2024 · Shortlist", or with a category "The Ezra Klein Show 2025 · Brian Eno · Oct. 3":
 * what Why I want to read it starts as. The category part was added Nov 3 (Kristi); drop it here to undo.
 */
export const whyFor = (list: string, year: number, e: Pick<ListEntry, 'level' | 'category'>) =>
  [`${list} ${year}`, levelName(e.level) || null, e.category?.trim() || null].filter(Boolean).join(' · ');

/** "Ann Author and Bo Writer; Cy Third" → three names (not split on commas, so "Jr." stays). */
export const authorsOf = (printed: string) =>
  printed
    .split(/;| and | & /)
    .map((s) => s.trim())
    .filter(Boolean);

/**
 * The literary genre to use for a list's books the catalog doesn't know: the one most of its known
 * books share, when at least three are known and four in five agree; otherwise null (you're asked).
 */
export function literaryGuess(entries: Pick<ListEntry, 'literary'>[]): Literary | null {
  const known = entries.map((e) => e.literary).filter((l): l is string => Boolean(l));
  if (known.length < 3) return null;
  const tally = new Map<string, number>();
  for (const l of known) tally.set(l, (tally.get(l) ?? 0) + 1);
  const [top, n] = [...tally.entries()].sort((a, b) => b[1] - a[1])[0]!;
  return n / known.length >= 0.8 ? (top as Literary) : null;
}

const failed = (notice: Notice, what: string, e: unknown) => notice({ message: `${what}: ${e instanceof Error ? e.message : String(e)}`, done: false });

/** Not for me, ticked on a row: on your list as passed on in Discover, with Undo. */
export async function passOn(notice: Notice, e: ListEntry, literary: Literary, why: string): Promise<boolean> {
  try {
    const added = await addBook({ title: e.title, authors: authorsOf(e.author), literary_genre: e.literary ?? literary, shelf: 'not_for_me', why_read: why, not_for_me: { chips: [] } }, e.id);
    notice({
      message: `${e.title}: Not for me`,
      undo: () =>
        void undoAdd(added.actions)
          .then(() => notice({ message: `Undone: ${e.title} isn’t on your list` }))
          .catch((x) => failed(notice, 'Couldn’t undo', x)),
    });
    return true;
  } catch (x) {
    failed(notice, 'Not passed on', x);
    booksChanged();
    return false;
  }
}

export type BulkWhere = 'future_reads' | 'up_next' | 'none';
export const BULK_WHERE: [BulkWhere, string][] = [
  ['future_reads', 'Future reads, at the bottom'],
  ['up_next', 'End of Up Next'],
  ['none', 'No shelf yet'],
];

/** Several at once: one import named for the list and year, then their entries linked. */
export async function addSeveral(notice: Notice, list: string, year: number, entries: ListEntry[], where: BulkWhere, literary: Literary | null): Promise<boolean> {
  const items = entries.map((e, i) => ({
    seq: i + 1,
    line: `${e.title} · ${e.author}`,
    outcome: 'add',
    book: {
      ...(e.workId ? { work_id: e.workId } : {}),
      title: e.title,
      authors: authorsOf(e.author),
      literary_genre: e.literary ?? literary,
      shelf: where,
      why_read: whyFor(list, year, e),
    },
  }));
  try {
    const res = await runImport(`${list} ${year}`, items);
    if (supabase) await supabase.rpc('list_link_added', { entries: entries.map((e) => e.id) });
    booksChanged();
    const n = res.added.length;
    const shelf = BULK_WHERE.find(([w]) => w === where)![1].replace(', at the bottom', '');
    // Looked up straight away (Kristi, Nov 3); the one notice with Undo import waits for it.
    let looked = '';
    if (n) {
      notice({ message: `${fmtNum(n)} ${n === 1 ? 'book' : 'books'} added; looking them up…`, done: false });
      looked = await fillNow(res.added.map((a) => a.book));
    }
    notice({
      message: (res.failed.length ? `${fmtNum(n)} added to ${shelf}; ${fmtNum(res.failed.length)} couldn’t be added: ${res.failed[0]!.message}` : `${fmtNum(n)} ${n === 1 ? 'book' : 'books'} added to ${shelf}`) + (looked ? `. ${looked}` : ''),
      done: n > 0,
      undo: n
        ? () =>
            void undoImport(res.batch)
              .then((u) => notice({ message: u.kept.length ? `Undone, except ${fmtNum(u.kept.length)} changed since` : 'Undone: the import is gone' }))
              .catch((x) => failed(notice, 'Couldn’t undo', x))
        : undefined,
    });
    return n > 0;
  } catch (x) {
    failed(notice, 'Not added', x);
    booksChanged();
    return false;
  }
}

/**
 * After Select several (Kristi, Nov 3): each added book is looked up straight away, three at a
 * time, rather than waiting for the night: covers, summaries, IDs. What can't be settled now (several
 * matches, none, a site not answering) is left for the night, as for any new book.
 */
export async function fillNow(bookIds: string[]): Promise<string> {
  if (!supabase || !bookIds.length) return '';
  const { data } = await supabase.from('books').select('id, work_id').in('id', bookIds);
  const books = ((data as { id: string; work_id: string }[] | null) ?? []).filter((b) => b.work_id);
  let filled = 0;
  let later = 0;
  const queue = [...books];
  const worker = async () => {
    for (let b = queue.shift(); b; b = queue.shift()) {
      try {
        const r = await lookUpNow(b.id, b.work_id);
        if (r === 'filled') filled += 1;
        else if (r === 'tonight') later += 1;
      } catch {
        later += 1;
      }
    }
  };
  await Promise.all([worker(), worker(), worker()]);
  booksChanged();
  return `${fmtNum(filled)} filled in now${later ? `, ${fmtNum(later)} left for tonight’s look-up` : ''}`;
}
